import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

export const CapturePdfPreview = ({
  blob,
  label,
  thumbnail = false,
  fit = false,
}: {
  blob: Blob;
  label: string;
  thumbnail?: boolean;
  fit?: boolean;
}) => {
  const { t } = useTranslation();
  const [document, setDocument] = useState<PDFDocumentProxy>();
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(thumbnail ? 104 : 640);
  const [height, setHeight] = useState(300);
  const [rendering, setRendering] = useState(true);
  const container = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const controls = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let canceled = false;
    let task: PDFDocumentLoadingTask | undefined;
    setDocument(undefined);
    setFailed(false);
    setPage(1);
    const load = async () => {
      const [{ loadCapturePdf }, bytes] = await Promise.all([import('./pdf-renderer'), blob.arrayBuffer()]);
      if (canceled) return;
      task = loadCapturePdf(bytes);
      const result = await task.promise;
      if (!canceled) setDocument(result);
    };
    void load().catch(() => {
      if (!canceled) setFailed(true);
    });
    return () => {
      canceled = true;
      void task?.destroy().catch(() => undefined);
    };
  }, [blob]);

  useEffect(() => {
    if (thumbnail || !container.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(1, Math.min(1000, entry.contentRect.width)));
      // Standalone height follows the spinner/canvas; only a fixed review viewport may drive height fitting.
      if (fit) setHeight(Math.max(80, entry.contentRect.height - (controls.current?.offsetHeight ?? 64) - 8));
    });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [thumbnail, fit, document]);

  useEffect(() => {
    if (!document || !canvas.current) return;
    let canceled = false;
    let task: RenderTask | undefined;
    const target = canvas.current;
    setRendering(true);
    setFailed(false);
    const render = async () => {
      const pdfPage = await document.getPage(page);
      if (canceled) return;
      const original = pdfPage.getViewport({ scale: 1, rotation: (pdfPage.rotate + rotation) % 360 });
      const displayScale = thumbnail
        ? Math.min(104 / original.width, 112 / original.height)
        : Math.min(width / original.width, fit ? height / original.height : Infinity) * zoom;
      const density = Math.min(window.devicePixelRatio || 1, 2);
      const scale = Math.min(
        displayScale * density,
        Math.sqrt(8_000_000 / (original.width * original.height)),
        4096 / Math.max(original.width, original.height),
      );
      const viewport = pdfPage.getViewport({ scale, rotation: (pdfPage.rotate + rotation) % 360 });
      target.width = Math.ceil(viewport.width);
      target.height = Math.ceil(viewport.height);
      target.style.width = `${original.width * displayScale}px`;
      target.style.height = `${original.height * displayScale}px`;
      task = pdfPage.render({ canvas: target, viewport });
      await task.promise;
      if (!canceled) setRendering(false);
    };
    void render().catch(() => {
      if (!canceled) {
        setFailed(true);
        setRendering(false);
      }
    });
    return () => {
      canceled = true;
      task?.cancel();
    };
  }, [document, page, rotation, width, height, zoom, thumbnail, fit]);

  return (
    <Box
      ref={container}
      sx={{
        width: '100%',
        minWidth: 0,
        ...(fit ? { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 } : {}),
      }}
    >
      {!thumbnail && document && (
        <Stack
          ref={controls}
          direction="row"
          gap={0.5}
          flexWrap="wrap"
          alignItems="center"
          mb={1}
          sx={{ flexShrink: 0, '& button': { minWidth: 32, px: 0.5 } }}
        >
          <Button
            aria-label={t('copilot.capture.previousPage')}
            size="small"
            disabled={page === 1}
            onClick={() => setPage((value) => value - 1)}
          >
            ‹
          </Button>
          <Typography variant="caption" aria-live="polite">
            {t('copilot.capture.pdfPage', { page, total: document.numPages })}
          </Typography>
          <Button
            aria-label={t('copilot.capture.nextPage')}
            size="small"
            disabled={page === document.numPages}
            onClick={() => setPage((value) => value + 1)}
          >
            ›
          </Button>
          <Button
            size="small"
            disabled={zoom <= 1}
            onClick={() => setZoom((value) => Math.max(1, value - 0.5))}
            aria-label={t('copilot.capture.zoomOut')}
          >
            −
          </Button>
          <Typography variant="caption">{Math.round(zoom * 100)}%</Typography>
          <Button
            size="small"
            disabled={zoom >= 3}
            onClick={() => setZoom((value) => Math.min(3, value + 0.5))}
            aria-label={t('copilot.capture.zoomIn')}
          >
            +
          </Button>
          <Button size="small" onClick={() => setRotation((value) => (value + 90) % 360)}>
            {t('copilot.capture.rotate')}
          </Button>
        </Stack>
      )}
      {failed && (
        <Alert severity="warning" sx={{ p: thumbnail ? 0.5 : 1 }}>
          {t('copilot.capture.previewError')}
        </Alert>
      )}
      <Box
        sx={{
          display: failed ? 'none' : 'block',
          overflow: 'auto',
          maxHeight: thumbnail ? 112 : fit ? '100%' : '62vh',
          textAlign: 'center',
          minHeight: thumbnail ? 80 : fit ? 0 : 160,
          ...(fit ? { flex: 1 } : {}),
        }}
      >
        {(!document || rendering) && (
          <CircularProgress size={thumbnail ? 20 : 32} aria-label={t('copilot.capture.loadingPreview')} />
        )}
        <canvas
          ref={canvas}
          role="img"
          aria-label={t('copilot.capture.pdfPageLabel', { label, page })}
          style={{ display: document && !rendering ? 'block' : 'none', margin: '0 auto' }}
        />
      </Box>
    </Box>
  );
};
